import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Network, ZoomIn, ZoomOut, RefreshCw, Hash, User } from 'lucide-react';

interface SocialPost {
  id: string;
  platform: string;
  text: string;
  author: string;
  author_id: string;
  author_verified: boolean;
  author_followers: number;
  timestamp: string;
  hashtags: string[];
  mentions: string[];
  likes: number;
  shares: number;
  comments: number;
  views: number;
  sentiment: 'positive' | 'negative' | 'neutral' | 'unknown';
  sentiment_confidence: number;
  language: string;
  keywords: string[];
  metadata: Record<string, any>;
}

interface GraphNode {
  id: string;
  type: 'author' | 'hashtag' | 'topic';
  label: string;
  platform?: string;
  sentiment?: string;
  followers?: number;
  postCount: number;
  engagement: number;
  botScore: number;
  verified?: boolean;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface GraphLink {
  source: string | GraphNode;
  target: string | GraphNode;
  weight: number;
  type: 'author-hashtag' | 'co-mention';
}

interface SocialNetworkGraphProps {
  posts: SocialPost[];
  height?: number;
  onNodeClick?: (node: GraphNode, posts: SocialPost[]) => void;
}

const PLATFORM_COLORS: Record<string, string> = {
  twitter: '#1DA1F2',
  x: '#000000',
  instagram: '#E1306C',
  facebook: '#1877F2',
  tiktok: '#010101',
  reddit: '#FF4500',
  youtube: '#FF0000',
  telegram: '#2CA5E0',
  default: '#6366F1',
};

const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#22c55e',
  negative: '#ef4444',
  neutral: '#94a3b8',
  unknown: '#64748b',
};

function computeBotScore(posts: SocialPost[], followers: number): number {
  if (posts.length === 0) return 0;
  const totalEngagement = posts.reduce((s, p) => s + p.likes + p.shares + p.comments, 0);
  const avgEngagement = totalEngagement / posts.length;
  const engagementRate = followers > 0 ? avgEngagement / followers : 0;
  // Very low engagement rate despite many followers → suspicious
  const lowEngagementSignal = engagementRate < 0.0005 && followers > 1000 ? 0.4 : 0;
  // High post count in short window → suspicious
  const highVolumeSignal = posts.length > 20 ? 0.3 : posts.length > 10 ? 0.15 : 0;
  // No verified badge + round follower number → slightly suspicious
  const unverifiedSignal = !posts[0]?.author_verified && followers % 1000 === 0 && followers > 0 ? 0.15 : 0;
  return Math.min(1, lowEngagementSignal + highVolumeSignal + unverifiedSignal);
}

const SocialNetworkGraph: React.FC<SocialNetworkGraphProps> = ({ posts, height = 600, onNodeClick }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [showHashtags, setShowHashtags] = useState(true);
  const [nodeCount, setNodeCount] = useState({ authors: 0, hashtags: 0, links: 0 });

  const buildGraph = useCallback((): { nodes: GraphNode[]; links: GraphLink[] } => {
    const authorMap = new Map<string, { posts: SocialPost[]; followers: number }>();
    const hashtagCount = new Map<string, number>();
    const coOccurrence = new Map<string, Map<string, number>>(); // hashtag co-occurrence

    // Apply filters
    let filteredPosts = posts;
    if (filterSentiment !== 'all') filteredPosts = filteredPosts.filter(p => p.sentiment === filterSentiment);
    if (filterPlatform !== 'all') filteredPosts = filteredPosts.filter(p => p.platform === filterPlatform);

    filteredPosts.forEach(post => {
      const key = post.author_id || post.author;
      if (!authorMap.has(key)) authorMap.set(key, { posts: [], followers: post.author_followers || 0 });
      authorMap.get(key)!.posts.push(post);

      post.hashtags?.forEach(tag => {
        const t = tag.toLowerCase().replace(/^#/, '');
        hashtagCount.set(t, (hashtagCount.get(t) || 0) + 1);

        // Co-occurrence tracking
        post.hashtags?.forEach(other => {
          if (other === tag) return;
          const o = other.toLowerCase().replace(/^#/, '');
          if (!coOccurrence.has(t)) coOccurrence.set(t, new Map());
          coOccurrence.get(t)!.set(o, (coOccurrence.get(t)!.get(o) || 0) + 1);
        });
      });
    });

    // Build author nodes (limit to top 60 by engagement)
    const authorNodes: GraphNode[] = Array.from(authorMap.entries())
      .map(([authorId, { posts: aPosts, followers }]) => {
        const post = aPosts[0];
        const engagement = aPosts.reduce((s, p) => s + p.likes + p.shares + p.comments, 0);
        const dominant = aPosts.reduce((acc, p) => {
          acc[p.sentiment] = (acc[p.sentiment] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        const dominantSentiment = Object.entries(dominant).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
        return {
          id: `author:${authorId}`,
          type: 'author' as const,
          label: post.author,
          platform: post.platform,
          sentiment: dominantSentiment,
          followers,
          postCount: aPosts.length,
          engagement,
          botScore: computeBotScore(aPosts, followers),
          verified: post.author_verified,
        };
      })
      .sort((a, b) => b.engagement - a.engagement)
      .slice(0, 60);

    // Build hashtag nodes (top 30 by frequency)
    const hashtagNodes: GraphNode[] = showHashtags
      ? Array.from(hashtagCount.entries())
          .filter(([, c]) => c >= 2)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 30)
          .map(([tag, count]) => ({
            id: `hashtag:${tag}`,
            type: 'hashtag' as const,
            label: `#${tag}`,
            postCount: count,
            engagement: count,
            botScore: 0,
          }))
      : [];

    const nodeIds = new Set([...authorNodes.map(n => n.id), ...hashtagNodes.map(n => n.id)]);
    const links: GraphLink[] = [];

    // Author → hashtag links
    if (showHashtags) {
      filteredPosts.forEach(post => {
        const authorKey = `author:${post.author_id || post.author}`;
        if (!nodeIds.has(authorKey)) return;
        post.hashtags?.forEach(tag => {
          const t = tag.toLowerCase().replace(/^#/, '');
          const hashKey = `hashtag:${t}`;
          if (nodeIds.has(hashKey)) {
            links.push({ source: authorKey, target: hashKey, weight: 1, type: 'author-hashtag' });
          }
        });
      });
    }

    // Author → Author co-mention links (via @mentions)
    const mentionMap = new Map<string, string>(); // username → author node id
    authorMap.forEach((_, authorId) => {
      const post = authorMap.get(authorId)!.posts[0];
      if (post) mentionMap.set(post.author.toLowerCase(), `author:${authorId}`);
    });

    filteredPosts.forEach(post => {
      const authorKey = `author:${post.author_id || post.author}`;
      if (!nodeIds.has(authorKey)) return;
      post.mentions?.forEach(mention => {
        const targetKey = mentionMap.get(mention.toLowerCase().replace(/^@/, ''));
        if (targetKey && targetKey !== authorKey && nodeIds.has(targetKey)) {
          links.push({ source: authorKey, target: targetKey, weight: 2, type: 'co-mention' });
        }
      });
    });

    return { nodes: [...authorNodes, ...hashtagNodes], links };
  }, [posts, filterSentiment, filterPlatform, showHashtags]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const { nodes, links } = buildGraph();
    setNodeCount({
      authors: nodes.filter(n => n.type === 'author').length,
      hashtags: nodes.filter(n => n.type === 'hashtag').length,
      links: links.length,
    });

    const width = containerRef.current.clientWidth || 900;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg.attr('width', width).attr('height', height);

    // Zoom layer
    const g = svg.append('g');
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => g.attr('transform', event.transform));
    svg.call(zoom);

    // Arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 20)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#475569');

    // Simulation
    if (simulationRef.current) simulationRef.current.stop();
    simulationRef.current = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links)
        .id(d => d.id)
        .distance(d => d.type === 'author-hashtag' ? 80 : 120)
        .strength(d => d.type === 'co-mention' ? 0.5 : 0.3))
      .force('charge', d3.forceManyBody<GraphNode>().strength(d => d.type === 'hashtag' ? -60 : -120))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide<GraphNode>().radius(d => nodeRadius(d) + 8));

    // Links
    const link = g.append('g').attr('class', 'links')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', d => d.type === 'co-mention' ? '#7c3aed' : '#334155')
      .attr('stroke-opacity', d => d.type === 'co-mention' ? 0.6 : 0.3)
      .attr('stroke-width', d => d.type === 'co-mention' ? 2 : 1)
      .attr('marker-end', d => d.type === 'co-mention' ? 'url(#arrow)' : null);

    // Node groups
    const node = g.append('g').attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('cursor', 'pointer')
      .call(d3.drag<SVGGElement, GraphNode>()
        .on('start', (event, d) => {
          if (!event.active) simulationRef.current?.alphaTarget(0.3).restart();
          d.fx = d.x; d.fy = d.y;
        })
        .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
        .on('end', (event, d) => {
          if (!event.active) simulationRef.current?.alphaTarget(0);
          d.fx = null; d.fy = null;
        }) as any
      )
      .on('click', (_, d) => {
        setSelectedNode(d);
        const relatedPosts = posts.filter(p =>
          d.type === 'author'
            ? (p.author_id || p.author) === d.id.replace('author:', '')
            : p.hashtags?.some(t => `hashtag:${t.toLowerCase().replace(/^#/, '')}` === d.id)
        );
        onNodeClick?.(d, relatedPosts);
      });

    // Bot score outer ring (dashed red if bot score > 0.5)
    node.filter(d => d.botScore > 0.5)
      .append('circle')
      .attr('r', d => nodeRadius(d) + 5)
      .attr('fill', 'none')
      .attr('stroke', '#ef4444')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4,3')
      .attr('opacity', 0.8);

    // Platform color ring (author nodes)
    node.filter(d => d.type === 'author')
      .append('circle')
      .attr('r', d => nodeRadius(d) + 2)
      .attr('fill', 'none')
      .attr('stroke', d => PLATFORM_COLORS[d.platform || 'default'] || PLATFORM_COLORS.default)
      .attr('stroke-width', 2.5)
      .attr('opacity', 0.9);

    // Main circle
    node.append('circle')
      .attr('r', nodeRadius)
      .attr('fill', d => {
        if (d.type === 'hashtag') return '#1e293b';
        return SENTIMENT_COLORS[d.sentiment || 'unknown'];
      })
      .attr('fill-opacity', d => d.type === 'hashtag' ? 0.9 : 0.75)
      .attr('stroke', d => d.type === 'hashtag' ? '#475569' : 'transparent')
      .attr('stroke-width', 1);

    // Verified badge (star)
    node.filter(d => d.verified === true)
      .append('text')
      .attr('dy', d => -nodeRadius(d) + 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .text('✓')
      .attr('fill', '#60a5fa');

    // Labels
    node.append('text')
      .attr('dy', d => nodeRadius(d) + 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', d => d.type === 'hashtag' ? '9px' : '10px')
      .attr('fill', '#94a3b8')
      .text(d => {
        const lbl = d.label;
        return lbl.length > 14 ? lbl.slice(0, 12) + '…' : lbl;
      });

    // Hashtag icon center
    node.filter(d => d.type === 'hashtag')
      .append('text')
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', d => `${Math.min(14, nodeRadius(d))}px`)
      .attr('fill', '#64748b')
      .text('#');

    // Simulation tick
    simulationRef.current.on('tick', () => {
      link
        .attr('x1', d => (d.source as GraphNode).x!)
        .attr('y1', d => (d.source as GraphNode).y!)
        .attr('x2', d => (d.target as GraphNode).x!)
        .attr('y2', d => (d.target as GraphNode).y!);
      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    return () => { simulationRef.current?.stop(); };
  }, [posts, filterSentiment, filterPlatform, showHashtags, height, buildGraph]);

  const nodeRadius = (d: GraphNode) => {
    if (d.type === 'hashtag') return Math.max(8, Math.min(22, d.postCount * 2.5));
    const base = Math.max(10, Math.min(30, Math.log10(Math.max(1, d.followers || 1)) * 5));
    return base;
  };

  const handleZoom = (direction: 'in' | 'out') => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.2, 4]);
    svg.transition().duration(300).call(zoom.scaleBy as any, direction === 'in' ? 1.4 : 0.7);
  };

  const resetSimulation = () => {
    simulationRef.current?.alpha(0.8).restart();
  };

  const platforms = [...new Set(posts.map(p => p.platform))];

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterSentiment} onValueChange={setFilterSentiment}>
          <SelectTrigger className="w-36 h-8 text-xs">
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sentiments</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="negative">Negative</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue placeholder="Platform" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            {platforms.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button
          variant={showHashtags ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowHashtags(v => !v)}
          className="h-8 text-xs"
        >
          <Hash className="w-3 h-3 mr-1" />
          Hashtags
        </Button>

        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => handleZoom('in')} className="h-8 w-8 p-0">
            <ZoomIn className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleZoom('out')} className="h-8 w-8 p-0">
            <ZoomOut className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm" onClick={resetSimulation} className="h-8 w-8 p-0">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>

        <div className="flex items-center gap-3 ml-auto text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><User className="w-3 h-3" /> {nodeCount.authors} accounts</span>
          <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {nodeCount.hashtags} hashtags</span>
          <span>{nodeCount.links} connections</span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-green-500 opacity-75" />
          <span>Positive sentiment</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500 opacity-75" />
          <span>Negative sentiment</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-slate-400 opacity-75" />
          <span>Neutral</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-red-500 border-dashed" />
          <span>Bot-like account</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-1 bg-violet-600" />
          <span>Co-mention link</span>
        </div>
      </div>

      {/* Graph + Node Panel */}
      <div className="flex gap-4">
        <div ref={containerRef} className="flex-1 bg-slate-950 rounded-lg border border-slate-800 overflow-hidden relative">
          {posts.length === 0 ? (
            <div className="flex items-center justify-center" style={{ height }}>
              <div className="text-center text-muted-foreground">
                <Network className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No posts to visualize</p>
              </div>
            </div>
          ) : (
            <svg ref={svgRef} className="w-full" />
          )}
        </div>

        {/* Node detail panel */}
        {selectedNode && (
          <Card className="w-56 shrink-0 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                {selectedNode.type === 'author' ? <User className="w-4 h-4" /> : <Hash className="w-4 h-4" />}
                {selectedNode.type === 'author' ? 'Account' : 'Hashtag'}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-2">
              <p className="font-medium truncate">{selectedNode.label}</p>
              {selectedNode.platform && (
                <Badge style={{ backgroundColor: PLATFORM_COLORS[selectedNode.platform] || '#6366F1' }} className="text-white text-[10px]">
                  {selectedNode.platform}
                </Badge>
              )}
              {selectedNode.sentiment && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SENTIMENT_COLORS[selectedNode.sentiment] }} />
                  <span className="capitalize">{selectedNode.sentiment}</span>
                </div>
              )}
              {selectedNode.type === 'author' && (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Followers</span>
                    <span>{(selectedNode.followers || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Posts</span>
                    <span>{selectedNode.postCount}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Engagement</span>
                    <span>{selectedNode.engagement.toLocaleString()}</span>
                  </div>
                  {selectedNode.verified && <Badge variant="outline" className="text-blue-400 border-blue-400 text-[10px]">Verified</Badge>}
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Bot score</span>
                      <span className={selectedNode.botScore > 0.5 ? 'text-red-400' : selectedNode.botScore > 0.25 ? 'text-yellow-400' : 'text-green-400'}>
                        {(selectedNode.botScore * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${selectedNode.botScore * 100}%`,
                          backgroundColor: selectedNode.botScore > 0.5 ? '#ef4444' : selectedNode.botScore > 0.25 ? '#eab308' : '#22c55e',
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
              {selectedNode.type === 'hashtag' && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Occurrences</span>
                  <span>{selectedNode.postCount}</span>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-6 text-xs mt-1"
                onClick={() => setSelectedNode(null)}
              >
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SocialNetworkGraph;
